/**
 * src/components/providers/AppErrorBoundary.tsx
 * =============================================================================
 * Boundary global de errores no atrapados. Envuelve la app entera.
 *
 * Cuando algo falla en render:
 *  1. Sentry captura el error automáticamente (si hay DSN configurado).
 *  2. Se muestra una pantalla de fallback minimalista con un botón "Recargar".
 *
 * Es deliberadamente simple — no queremos exponer stack traces ni mensajes
 * técnicos al usuario. El detalle queda en Sentry.
 *
 * i18n: el fallback usa la instancia `i18n` directamente (i18n.t) en vez del
 * hook `useTranslation`. Razón: esta pantalla se pinta cuando el árbol de
 * React acaba de reventar — cuantas menos dependencias de contexto/hooks
 * tenga, menos probable es que el propio fallback falle también. La instancia
 * global ya está inicializada al importar `@/lib/i18n` en el arranque.
 * =============================================================================
 */

import { Sentry } from '@/lib/sentry'
import i18n from '@/lib/i18n'

export function AppErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      {children}
    </Sentry.ErrorBoundary>
  )
}

function ErrorFallback() {
  return (
    <div className="min-h-dvh flex flex-col items-center justify-center px-6 bg-background text-foreground">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="text-2xl font-bold tracking-tight">{i18n.t('errorApp.titulo')}</h1>
        <p className="text-sm text-muted-foreground">
          {i18n.t('errorApp.texto')}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center justify-center h-9 px-4 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium"
        >
          {i18n.t('errorApp.recargar')}
        </button>
      </div>
    </div>
  )
}
